import _ from 'lodash'
import { Radio, RadioItem, Spinner } from '@agro-club/agroclub-shared'
import { useAFormHandler } from 'analytics/hooks'
import { useFormik } from 'formik'
import { refetchFunc, useSingleEntity } from 'modules/domain/common/hooks'
import { CurrencyCode, Hedges, HedgeType } from 'modules/domain/export/types'
import { endpoints } from 'modules/endpoints'
import { apiClient } from 'modules/utils/httpClient'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { HrWithHeader } from 'views/components/Hr/Hr'
import { ResponsiveModal } from 'views/components/ResponsiveModal/ResponsiveModal'
import { SharedModalInputs } from 'views/components/SharedModal'
import { FormItemWrapper } from 'views/styled/common'
import { CommonForwardFields } from './CommonForwardFields'
import { CommonOptionFields, HedgingFields } from './CommonOptionFields'
import { CommonNaturalFields } from './CommonNaturalFields'

const SpinnerWrapper = styled.div`
  height: 584px;
  display: flex;
  align-items: center;
  justify-content: center;
`

type Props = {
  exportId: string
  exportHedgeId: string
  onClose: () => void
  onSuccess: refetchFunc
}

export const EditHedgingModal: FC<Props> = ({ onClose, exportId, exportHedgeId, onSuccess }) => {
  const { t } = useTranslation('export')
  const [, exportHedge, exportHedgeRefetch] = useSingleEntity<Hedges>(
    exportHedgeId ? endpoints.exportHedges(exportHedgeId) : undefined,
  )
  const { formProgress, formHandler } = useAFormHandler()

  const formik = useFormik<HedgingFields>({
    initialValues: {
      hedge_type: exportHedge?.hedge_type as HedgeType,
      export_contract: exportId,
      currency: exportHedge?.currency || CurrencyCode.USD,
      bank_deal_number: exportHedge?.bank_deal_number,
      option_volume: exportHedge?.option_volume,
      strike: exportHedge?.strike,
      expiration_date: exportHedge?.expiration_date,
      settlement_date: exportHedge?.settlement_date,
      option_bonus: exportHedge?.option_bonus,
      receipt_planned_date: exportHedge?.receipt_planned_date,
    },
    enableReinitialize: true,

    onSubmit: formHandler(
      () => {
        const { bank_deal_number, expiration_date, ...otherValues } = formik.values
        let dto
        switch (formik.values.hedge_type) {
          case HedgeType.option:
            dto = {
              ...otherValues,
              bank_deal_number,
              expiration_date,
            }
            break
          case HedgeType.forward:
            dto = otherValues
            break
          case HedgeType.natural:
            dto = _.pick(formik.values, [
              'hedge_type',
              'export_contract',
              'option_volume',
              'currency',
              'strike',
              'settlement_date',
            ])
            break
        }
        return apiClient.put(endpoints.exportHedges(exportHedge?.id), dto)
      },
      {
        onSuccess: () => {
          onSuccess()
          exportHedgeRefetch()
          onClose()
        },
        onError: (error) => {
          const { errors } = error
          formik.setErrors(errors)
        },
      },
    ),
  })

  let fieldsJSX
  switch (formik.values.hedge_type) {
    case HedgeType.option:
      fieldsJSX = <CommonOptionFields formik={formik} initNetRate={exportHedge?.net_rate} />
      break
    case HedgeType.forward:
      fieldsJSX = <CommonForwardFields formik={formik} />
      break
    case HedgeType.natural:
      fieldsJSX = <CommonNaturalFields formik={formik} />
      break
  }

  return (
    <ResponsiveModal
      id="editHedging"
      title={t('hedging')}
      size="small"
      onClose={onClose}
      onConfirm={formik.submitForm}
      confirmButtonText={t('common:save')}
      progress={formProgress}
    >
      <SharedModalInputs>
        {!exportHedge ? (
          <SpinnerWrapper>
            <Spinner />
          </SpinnerWrapper>
        ) : (
          <>
            <FormItemWrapper>
              <Radio onChange={(type) => formik.setFieldValue('hedge_type', type)} value={formik.values.hedge_type}>
                <RadioItem label={t('hedgeType.option')} value={HedgeType.option} disabled={true} />
                <RadioItem label={t('hedgeType.forward')} value={HedgeType.forward} disabled={true} />
                <RadioItem label={t('hedgeType.natural')} value={HedgeType.natural} disabled={true} />
              </Radio>
            </FormItemWrapper>

            <HrWithHeader text={t('calculationData')} />

            {fieldsJSX}
          </>
        )}
      </SharedModalInputs>
    </ResponsiveModal>
  )
}
